import { LitElement, html, nothing } from 'lit';
import { unsafeSVG } from 'lit/directives/unsafe-svg.js';
import { customElement, state } from 'lit/decorators.js';
import { api, type Game, type Team, type RosterImportRecord } from '../../services/api';
import { appState } from '../../store/AppState';

import './TeamManager';
import './SeasonManager';
import './PlayerManager';
import './RosterManager';
import './GamePitchEditor';
import './GamePlayerManager';
import './AuditLogViewer';
import './RosterImportWizard';
import './GameApprovalQueue';
import './RulesManager';
import './TeamInviteManager';
import './LeagueImportWizard';
import './CoachInviteList';
import './UnassignedUserList';

type AdminTab = 'teams' | 'seasons' | 'players' | 'rosters' | 'rules' | 'invitations' | 'coach-invites' | 'unassigned-users' | 'game-pitches' | 'game-players' | 'audit-log' | 'roster-import' | 'league-import' | 'approvals';

const VALID_ADMIN_TABS: AdminTab[] = ['teams', 'seasons', 'players', 'rosters', 'rules', 'invitations', 'coach-invites', 'unassigned-users', 'game-pitches', 'game-players', 'audit-log', 'roster-import', 'league-import', 'approvals'];

function isValidAdminTab(value: string | null): value is AdminTab {
  return value !== null && VALID_ADMIN_TABS.includes(value as AdminTab);
}

interface DashboardCard {
  id: AdminTab;
  label: string;
  description: string;
  iconPaths: string[];
}

interface DashboardGroup {
  name: string;
  cards: DashboardCard[];
}

const DASHBOARD_GROUPS: DashboardGroup[] = [
  {
    name: 'Setup',
    cards: [
      {
        id: 'teams', label: 'Teams',
        description: 'Create and manage league teams',
        iconPaths: ['M18 18.72a9.094 9.094 0 0 0 3.741-.479 3 3 0 0 0-4.682-2.72m.94 3.198.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0 1 12 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 0 1 6 18.719m12 0a5.971 5.971 0 0 0-.941-3.197m0 0A5.995 5.995 0 0 0 12 12.75a5.995 5.995 0 0 0-5.058 2.772m0 0a3 3 0 0 0-4.681 2.72 8.986 8.986 0 0 0 3.74.477m.94-3.197a5.971 5.971 0 0 0-.94 3.197M15 6.75a3 3 0 1 1-6 0 3 3 0 0 1 6 0Zm6 3a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0Zm-13.5 0a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0Z'],
      },
      {
        id: 'seasons', label: 'Seasons',
        description: 'Configure competition seasons',
        iconPaths: ['M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5'],
      },
      {
        id: 'players', label: 'Players',
        description: 'Manage player profiles and details',
        iconPaths: ['M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z'],
      },
      {
        id: 'rosters', label: 'Rosters',
        description: 'Assign players to team rosters',
        iconPaths: ['M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 0 0 2.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 0 0-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 0 0 .75-.75 2.25 2.25 0 0 0-.1-.664m-5.8 0A2.251 2.251 0 0 1 13.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25ZM6.75 12h.008v.008H6.75V12Zm0 3h.008v.008H6.75V15Zm0 3h.008v.008H6.75V18Z'],
      },
      {
        id: 'rules', label: 'Rules',
        description: 'Set pitch limits and rest requirements',
        iconPaths: ['M12 6.042A8.967 8.967 0 0 0 6 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 0 1 6 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 0 1 6-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0 0 18 18a8.967 8.967 0 0 0-6 2.292m0-14.25v14.25'],
      },
    ],
  },
  {
    name: 'Import Data',
    cards: [
      {
        id: 'roster-import', label: 'Team Import',
        description: 'Import a single team roster from CSV',
        iconPaths: ['M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5m-13.5-9L12 3m0 0 4.5 4.5M12 3v13.5'],
      },
      {
        id: 'league-import', label: 'League Import',
        description: 'Bulk import rosters from league XLSX',
        iconPaths: ['M2.25 21h19.5m-18-18v18m10.5-18v18m6-13.5V21M6.75 6.75h.75m-.75 3h.75m-.75 3h.75m3-6h.75m-.75 3h.75m-.75 3h.75M6.75 21v-3.375c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21M3 3h12m-.75 4.5H21m-3.75 3.75h.008v.008h-.008v-.008Zm0 3h.008v.008h-.008v-.008Zm0 3h.008v.008h-.008v-.008Z'],
      },
    ],
  },
  {
    name: 'Games',
    cards: [
      {
        id: 'approvals', label: 'Approvals',
        description: 'Review and approve submitted games',
        iconPaths: ['M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z'],
      },
      {
        id: 'game-pitches', label: 'Edit Game',
        description: 'View and edit game pitch counts',
        iconPaths: ['M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 0 1 3 19.875v-6.75ZM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V8.625ZM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V4.125Z'],
      },
      {
        id: 'game-players', label: 'Game Lineup',
        description: 'Manage player participation per game',
        iconPaths: ['M18 7.5v3m0 0v3m0-3h3m-3 0h-3m-2.25-4.125a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0ZM3 19.235v-.11a6.375 6.375 0 0 1 12.75 0v.109A12.318 12.318 0 0 1 9.374 21c-2.331 0-4.512-.645-6.374-1.766Z'],
      },
      {
        id: 'audit-log', label: 'Audit Log',
        description: 'Track changes and submission history',
        iconPaths: ['M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m5.231 13.481L15 17.25m-4.5-15H5.625c-.621 0-1.125.504-1.125 1.125v16.5c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Zm3.75 11.625a2.625 2.625 0 1 1-5.25 0 2.625 2.625 0 0 1 5.25 0Z'],
      },
    ],
  },
  {
    name: 'Access',
    cards: [
      {
        id: 'invitations', label: 'Team Invites',
        description: 'Invite coaches to manage their teams',
        iconPaths: ['M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75'],
      },
      {
        id: 'coach-invites', label: 'Coach Invites',
        description: 'Send email invitations to imported coaches',
        iconPaths: ['M6 12 3.269 3.125A59.769 59.769 0 0 1 21.485 12 59.768 59.768 0 0 1 3.27 20.875L5.999 12Zm0 0h7.5'],
      },
      {
        id: 'unassigned-users', label: 'Unassigned Users',
        description: 'Assign signed-up users who never joined a team',
        iconPaths: ['M18 7.5v3m0 0v3m0-3h3m-3 0h-3m-2.25-4.125a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0ZM3 19.235v-.11a6.375 6.375 0 0 1 12.75 0v.109A12.318 12.318 0 0 1 9.374 21c-2.331 0-4.512-.645-6.374-1.766Z'],
      },
    ],
  },
];

@customElement('admin-view')
export class AdminView extends LitElement {
  createRenderRoot() {
    return this;
  }

  @state()
  private activeTab: AdminTab | null = null;

  @state()
  private games: Game[] = [];

  @state()
  private selectedGameId = '';

  @state()
  private loadingGames = false;

  // Roster import state
  @state()
  private teams: Team[] = [];

  @state()
  private selectedTeamForImport = '';


  @state()
  private importHistory: RosterImportRecord[] = [];

  @state()
  private pendingApprovalCount = 0;

  @state()
  private searchQuery = '';

  /**
   * Search filter for the game picker (used by game-pitches, game-players,
   * audit-log sub-views). Matches against name, team names, location,
   * formatted date, and game ID substring. Local-only — list is already
   * in memory after the initial loadGames() call.
   */
  @state()
  private gameSearchQuery = '';

  connectedCallback() {
    super.connectedCallback();
    this.loadGames();
    this.loadTeams();
    this.loadPendingApprovalCount();

    // Restore admin tab from URL if present
    const viewFromUrl = appState.getViewFromUrl();
    if (isValidAdminTab(viewFromUrl)) {
      this.activeTab = viewFromUrl;
    }

    // Listen for navigate-approvals events from SyncIndicator
    window.addEventListener('navigate-approvals', this.handleNavigateApprovals);
    // Refresh teams when they change in TeamManager
    this.addEventListener('teams-changed', this.handleTeamsChanged);
    this.addEventListener('game-load-failed', this.handleGameLoadFailed as EventListener);
    // Handle browser back/forward navigation
    window.addEventListener('popstate', this.handlePopState);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('navigate-approvals', this.handleNavigateApprovals);
    this.removeEventListener('teams-changed', this.handleTeamsChanged);
    this.removeEventListener('game-load-failed', this.handleGameLoadFailed as EventListener);
    window.removeEventListener('popstate', this.handlePopState);
  }

  private handleTeamsChanged = () => {
    this.loadTeams();
  };

  // When the per-game editor reports it couldn't load (typically because
  // another admin deleted the game between this admin's list-load and
  // click), drop the stale selection and refresh the list so the deleted
  // entry disappears.
  private handleGameLoadFailed = () => {
    this.selectedGameId = '';
    this.loadGames();
  };

  private handleNavigateApprovals = () => {
    this.setTab('approvals');
  };

  private handlePopState = () => {
    const viewFromUrl = appState.getViewFromUrl();
    if (isValidAdminTab(viewFromUrl)) {
      this.activeTab = viewFromUrl;
    } else {
      this.activeTab = null;
    }
  };

  private async loadGames() {
    this.loadingGames = true;
    try {
      const games = await api.getGames();
      this.games = games || [];
      // Sort by date descending (most recent first)
      this.games.sort((a, b) =>
        new Date(b.game_date).getTime() - new Date(a.game_date).getTime()
      );
      // Do NOT auto-select a game — with 350+ games, admins should pick
      // explicitly from the searchable list rather than being dropped
      // into whatever happens to sort first.
    } catch (error) {
      console.error('Failed to load games:', error);
    } finally {
      this.loadingGames = false;
    }
  }

  /**
   * Filter games by the search query. Matches (case-insensitive) against:
   *   - game name
   *   - home/away team names
   *   - location
   *   - formatted date (locale string + ISO date)
   *   - game ID substring (admins paste UUIDs from logs/links)
   */
  private getFilteredGames(): Game[] {
    const q = this.gameSearchQuery.trim().toLowerCase();
    if (!q) return this.games;
    return this.games.filter(g => {
      const dateStr = new Date(g.game_date).toLocaleDateString().toLowerCase();
      const isoDate = g.game_date.slice(0, 10).toLowerCase();
      return (
        g.name.toLowerCase().includes(q) ||
        (g.home_team_name ?? '').toLowerCase().includes(q) ||
        (g.away_team_name ?? '').toLowerCase().includes(q) ||
        (g.location ?? '').toLowerCase().includes(q) ||
        dateStr.includes(q) ||
        isoDate.includes(q) ||
        g.id.toLowerCase().includes(q)
      );
    });
  }

  private async loadTeams() {
    try {
      this.teams = (await api.getTeams()) || [];
    } catch (error) {
      console.error('Failed to load teams:', error);
    }
  }

  private async loadPendingApprovalCount() {
    try {
      const pending = (await api.getPendingGames()) || [];
      this.pendingApprovalCount = pending.length;
    } catch {
      // Non-critical — don't block the admin view
    }
  }

  private async loadImportHistory() {
    if (!this.selectedTeamForImport) return;
    try {
      this.importHistory = await api.getImportHistory(this.selectedTeamForImport);
    } catch (error) {
      console.error('Failed to load import history:', error);
      this.importHistory = [];
    }
  }

  private handleImportTeamChange(e: Event) {
    this.selectedTeamForImport = (e.target as HTMLSelectElement).value;
    this.loadImportHistory();
  }

  private handleImportComplete() {
    this.loadImportHistory();
  }

  private setTab(tab: AdminTab | null) {
    const leavingApprovals = this.activeTab === 'approvals' && tab !== 'approvals';
    this.activeTab = tab;
    appState.updateUrlWithView(tab);

    // Refresh pending count when leaving approvals (user may have approved/rejected)
    if (leavingApprovals) {
      this.loadPendingApprovalCount();
    }
  }

  private isGameTab(): boolean {
    return this.activeTab !== null && ['game-pitches', 'game-players', 'audit-log'].includes(this.activeTab);
  }

  private getTabLabel(): string {
    for (const group of DASHBOARD_GROUPS) {
      const card = group.cards.find(c => c.id === this.activeTab);
      if (card) return card.label;
    }
    return '';
  }

  render() {
    return html`
      <div class="space-y-6 animate-fade-in">
        ${this.activeTab === null ? this.renderDashboard() : this.renderSubView()}
      </div>
    `;
  }

  private getFilteredGroups(): DashboardGroup[] {
    if (!this.searchQuery.trim()) {
      return DASHBOARD_GROUPS;
    }
    
    const query = this.searchQuery.toLowerCase().trim();
    return DASHBOARD_GROUPS.map(group => ({
      ...group,
      cards: group.cards.filter(card =>
        card.label.toLowerCase().includes(query) ||
        card.description.toLowerCase().includes(query)
      )
    })).filter(group => group.cards.length > 0);
  }

  private renderDashboard() {
    const filteredGroups = this.getFilteredGroups();
    
    return html`
      <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <h2 class="text-2xl font-bold">Admin</h2>
        
        <!-- Search filter -->
        <div class="relative w-full sm:w-64">
          <input
            type="text"
            class="input input-bordered input-sm w-full pl-9"
            placeholder="Search admin tools..."
            .value=${this.searchQuery}
            @input=${(e: Event) => this.searchQuery = (e.target as HTMLInputElement).value}
          />
          <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-base-content/40" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          ${this.searchQuery ? html`
            <button
              class="btn btn-ghost btn-xs btn-circle absolute right-2 top-1/2 -translate-y-1/2"
              @click=${() => this.searchQuery = ''}
            >
              <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          ` : nothing}
        </div>
      </div>

      ${filteredGroups.length === 0 ? html`
        <div class="text-center py-8 text-base-content/60">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-12 w-12 mx-auto mb-3 opacity-40" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
            <path stroke-linecap="round" stroke-linejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <p>No tools match "${this.searchQuery}"</p>
        </div>
      ` : filteredGroups.map(group => html`
        <div>
          <h3 class="text-xs font-medium text-base-content/60 uppercase tracking-wide mb-2">${group.name}</h3>
          <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            ${group.cards.map(card => html`
              <button
                class="flex items-start gap-3 p-4 rounded-xl bg-base-100 border border-base-300 text-left shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all cursor-pointer"
                @click=${() => this.setTab(card.id)}
              >
                <div class="w-10 h-10 flex items-center justify-center bg-primary/10 rounded-lg shrink-0 relative">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" class="w-5 h-5" style="stroke: oklch(var(--p)); stroke-width: 1.5;">
                    ${unsafeSVG(card.iconPaths.map(d => `<path stroke-linecap="round" stroke-linejoin="round" d="${d}" />`).join(''))}
                  </svg>
                  ${card.id === 'approvals' && this.pendingApprovalCount > 0 ? html`
                    <span class="absolute -top-1.5 -right-1.5 badge badge-warning badge-xs">${this.pendingApprovalCount}</span>
                  ` : nothing}
                </div>
                <div class="min-w-0">
                  <div class="font-medium text-sm">${card.label}</div>
                  <div class="text-xs text-base-content/60 mt-0.5">${card.description}</div>
                </div>
              </button>
            `)}
          </div>
        </div>
      `)}
    `;
  }

  private renderSubView() {
    return html`
      <!-- Breadcrumb -->
      <div class="flex items-center gap-2 text-sm">
        <button class="btn btn-ghost btn-sm gap-1" @click=${() => this.setTab(null)}>
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4">
            <path stroke-linecap="round" stroke-linejoin="round" d="M10.5 19.5 3 12m0 0 7.5-7.5M3 12h18" />
          </svg>
          Dashboard
        </button>
        <span class="text-base-content/40">/</span>
        <span class="font-medium">${this.getTabLabel()}</span>
      </div>

      <!-- Pending approvals alert -->
      ${this.pendingApprovalCount > 0 && this.activeTab !== 'approvals'
        ? html`
              <button
                class="w-full flex items-center gap-3 p-4 rounded-xl bg-gradient-to-r from-warning/10 to-warning/5 border border-warning/20 hover:border-warning/40 hover:shadow-md transition-all duration-200 group animate-fade-in"
                @click=${() => this.setTab('approvals')}
              >
                <div class="w-10 h-10 rounded-full bg-warning/20 flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform">
                  <span class="text-lg font-bold text-warning">${this.pendingApprovalCount}</span>
                </div>
                <div class="flex-1 text-left">
                  <p class="font-medium text-base-content">
                    ${this.pendingApprovalCount === 1 ? 'Game awaiting review' : 'Games awaiting review'}
                  </p>
                  <p class="text-sm text-base-content/60">Tap to review and approve</p>
                </div>
                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-base-content/40 group-hover:text-warning group-hover:translate-x-1 transition-all" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7" />
                </svg>
              </button>
            `
        : nothing}

      <!-- Game selector for game-related tabs -->
      ${this.isGameTab() ? this.renderGameSelector() : nothing}

      <!-- Tab content -->
      ${this.renderTabContent()}
    `;
  }

  private renderGameSelector() {
    if (this.loadingGames) {
      return html`
        <div class="flex items-center gap-2">
          <span class="loading loading-spinner loading-sm"></span>
          <span class="text-sm text-base-content/70">Loading games...</span>
        </div>
      `;
    }

    if (this.games.length === 0) {
      return html`
        <div class="alert alert-info">
          <span>No games found. Create a game first to manage pitch counts.</span>
        </div>
      `;
    }

    const selectedGame = this.games.find(g => g.id === this.selectedGameId);

    // Once a game is picked, show a compact "current selection" pill with
    // a Change link rather than the full list. Keeps the editor focused
    // on the chosen game without forcing a scroll past every option.
    if (selectedGame) {
      return html`
        <div class="flex items-start gap-2 p-3 rounded-lg bg-base-200/50 border border-base-300">
          <div class="flex-1 min-w-0">
            <div class="text-xs text-base-content/60 mb-0.5">Selected game</div>
            <div class="font-medium truncate">${selectedGame.name}</div>
            <div class="text-xs text-base-content/70 truncate">
              ${new Date(selectedGame.game_date).toLocaleDateString()}
              ${selectedGame.home_team_name && selectedGame.away_team_name
                ? ` · ${selectedGame.home_team_name} vs ${selectedGame.away_team_name}`
                : ''}
              ${selectedGame.location ? ` · ${selectedGame.location}` : ''}
            </div>
          </div>
          <button
            class="btn btn-ghost btn-sm shrink-0"
            data-testid="admin-game-change-btn"
            @click=${() => { this.selectedGameId = ''; this.gameSearchQuery = ''; }}
          >Change</button>
        </div>
      `;
    }

    return this.renderGameSearchList();
  }

  private renderGameSearchList() {
    const filtered = this.getFilteredGames();
    return html`
      <div class="space-y-3">
        <div>
          <label class="label py-1">
            <span class="label-text font-medium">Select a game</span>
            <span class="label-text-alt text-base-content/60">
              ${filtered.length} of ${this.games.length}
            </span>
          </label>
          <div class="relative">
            <input
              type="text"
              class="input input-bordered w-full pl-10"
              data-testid="admin-game-search-input"
              placeholder="Search by name, team, date, location, or game ID..."
              .value=${this.gameSearchQuery}
              @input=${(e: Event) => { this.gameSearchQuery = (e.target as HTMLInputElement).value; }}
            />
            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-base-content/40" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            ${this.gameSearchQuery ? html`
              <button
                class="btn btn-ghost btn-xs btn-circle absolute right-2 top-1/2 -translate-y-1/2"
                aria-label="Clear search"
                @click=${() => { this.gameSearchQuery = ''; }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            ` : nothing}
          </div>
        </div>

        ${filtered.length === 0
          ? html`
              <div class="text-center py-8 text-base-content/60 border border-dashed border-base-300 rounded-lg">
                <p>No games match "${this.gameSearchQuery}"</p>
              </div>
            `
          : html`
              <ul
                class="bg-base-100 border border-base-300 rounded-lg overflow-y-auto max-h-[60vh] divide-y divide-base-200"
                data-testid="admin-game-list"
                style="overscroll-behavior: contain; -webkit-overflow-scrolling: touch;"
              >
                ${filtered.map(game => html`
                  <li>
                    <button
                      class="w-full text-left px-3 py-3 hover:bg-base-200 active:bg-base-300 transition-colors"
                      data-testid="admin-game-list-item"
                      @click=${() => { this.selectedGameId = game.id; }}
                    >
                      <div class="font-medium truncate">${game.name}</div>
                      <div class="text-xs text-base-content/70 mt-0.5 truncate">
                        ${new Date(game.game_date).toLocaleDateString()}
                        ${game.home_team_name && game.away_team_name
                          ? ` · ${game.home_team_name} vs ${game.away_team_name}`
                          : ''}
                        ${game.location ? ` · ${game.location}` : ''}
                      </div>
                      <div class="text-[10px] text-base-content/40 font-mono mt-0.5 truncate">
                        ${game.id}
                      </div>
                    </button>
                  </li>
                `)}
              </ul>
            `}
      </div>
    `;
  }

  private renderTabContent() {
    switch (this.activeTab) {
      case 'teams':
        return html`<team-manager></team-manager>`;
      case 'seasons':
        return html`<season-manager></season-manager>`;
      case 'players':
        return html`<player-manager></player-manager>`;
      case 'rosters':
        return html`<roster-manager></roster-manager>`;
      case 'game-pitches':
        return this.selectedGameId
          ? html`<game-pitch-editor .gameId=${this.selectedGameId}></game-pitch-editor>`
          : nothing;
      case 'game-players':
        return this.selectedGameId
          ? html`<game-player-manager .gameId=${this.selectedGameId}></game-player-manager>`
          : nothing;
      case 'audit-log':
        return this.selectedGameId
          ? html`<audit-log-viewer .gameId=${this.selectedGameId}></audit-log-viewer>`
          : nothing;
      case 'roster-import':
        return this.renderRosterImport();
      case 'league-import':
        return html`<league-import-wizard></league-import-wizard>`;
      case 'rules':
        return html`<rules-manager></rules-manager>`;
      case 'approvals':
        return html`<game-approval-queue></game-approval-queue>`;
      case 'invitations':
        return html`<team-invite-manager></team-invite-manager>`;
      case 'coach-invites':
        return html`<coach-invite-list mode="standalone"></coach-invite-list>`;
      case 'unassigned-users':
        return html`<unassigned-user-list></unassigned-user-list>`;
    }
  }

  private renderRosterImport() {
    return html`
      <div class="space-y-4">
        <div class="form-control w-full max-w-xs">
          <label class="label">
            <span class="label-text">Select Team</span>
          </label>
          <select
            class="select select-bordered"
            .value=${this.selectedTeamForImport}
            @change=${this.handleImportTeamChange}
          >
            <option value="">Choose a team...</option>
            ${this.teams.map(team => html`
              <option value=${team.id}>${team.name}</option>
            `)}
          </select>
        </div>

        ${this.selectedTeamForImport
          ? html`
              <roster-import-wizard
                .teamId=${this.selectedTeamForImport}
                @import-complete=${this.handleImportComplete}
              ></roster-import-wizard>

              ${this.importHistory.length > 0
                ? html`
                    <div class="collapse collapse-arrow bg-base-100 shadow">
                      <input type="checkbox" />
                      <div class="collapse-title font-medium">
                        Import History (${this.importHistory.length})
                      </div>
                      <div class="collapse-content">
                        <div class="overflow-x-auto">
                          <table class="table table-sm">
                            <thead>
                              <tr>
                                <th>Date</th>
                                <th>File</th>
                                <th>Players</th>
                                <th>Status</th>
                              </tr>
                            </thead>
                            <tbody>
                              ${this.importHistory.map(record => html`
                                <tr>
                                  <td>${new Date(record.imported_at).toLocaleDateString()}</td>
                                  <td>${record.file_name}</td>
                                  <td>${record.player_count}</td>
                                  <td>
                                    <span class="badge ${record.status === 'completed' ? 'badge-success' : 'badge-error'}">
                                      ${record.status}
                                    </span>
                                  </td>
                                </tr>
                              `)}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  `
                : nothing}
            `
          : html`
              <div class="alert alert-info">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" class="stroke-current shrink-0 w-6 h-6">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
                <span>Select a team to import roster data from RAMP CSV export.</span>
              </div>
            `}
      </div>
    `;
  }
}
